/**
 * Route copy for `src/routes/documents` (keys: `routes.documents.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  classificationModal: {
    theAmountMustBeANumber: 'The amount must be a number.',
    couldNotSaveTryAgain: 'Could not save. Try again.',
    editTheClassification: 'Edit the classification',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    file: 'File:',
    type: 'Type',
    status: 'Status',
    none: '— None —',
    title: 'Title',
    invoiceHydroQuebecOctober: 'Invoice — Hydro-Québec October',
    provider: 'Provider',
    supplierCustomerName: 'Supplier / customer name',
    category: 'Category',
    telecomRentServices: 'Telecom, Rent, Services…',
    documentDate: 'Document date',
    amount: 'Amount',
    currency: 'Currency',
    folder: 'Folder',
    n2026Invoices: '2026/Invoices',
    labels: 'Labels',
    commaSeparated: 'Comma-separated',
    urgentDeductible: 'urgent, deductible',
    notes: 'Notes',
    referenceTerms: 'Reference, terms…',
  },
  documentsHubPage: {
    pricePerFile: '1 credit per classified file',
    autoClassifyLabel: 'Classify automatically (1 credit / file)',
    autoClassifyOffHint: 'Without auto-classification: free, basic classification (0 credits).',
    confirmTitle: 'Confirm upload',
    confirmMessage: '{{count}} files will be uploaded and classified automatically. Total cost: {{total}} credits.',
    confirmUpload: 'Upload',
    confirmCancel: 'Cancel',
    type: 'Type',
    month: 'Month',
    provider: 'Provider',
    category: 'Category',
    status: 'Status',
    source: 'Source',
    couldNotLoadTheDocuments: 'Could not load the documents.',
    couldNotLoadMore: 'Could not load more.',
    filenameUploadedAndClassified: '“{{filename}}” uploaded and classified.',
    filenameUploaded: '“{{filename}}” uploaded.',
    uploadFailed: 'Upload failed.',
    zipArchiveOfTheSelectionDownloaded: 'ZIP archive of the selection downloaded.',
    zipDownloadFailed: 'ZIP download failed.',
    zipArchiveDownloaded: 'ZIP archive downloaded.',
    downloadNotPossible: 'Download not possible.',
    documentsInvoices: 'Documents & invoices',
    classifyAndOrganizeYourReceivedInvoices: 'Classify and organize your received invoices, issued invoices, quotes, offers, service requests and contracts.',
    preparing: 'Preparing…',
    downloadTheSelectionZipV: 'Download the selection (ZIP){{v}}',
    downloadAllZip: 'Download all (ZIP)',
    emailTheSelection: 'Email the selection',
    emailTheDisplayedDocuments: 'Email the displayed documents',
    sendByEmailV: 'Send by email{{v}}',
    upload: 'Upload',
    filters: 'Filters',
    clearTheFilters: 'Clear the filters',
    removeTheFilter: 'Remove the filter',
    noFilterAvailableYet: 'No filter available yet.',
    searchTitleFileSupplierNotes: 'Search (title, file, supplier, notes…)',
    searchADocument: 'Search a document',
    dragAndDropYourDocumentsHere: 'Drag and drop your documents here, or',
    browseYourFiles: 'browse your files',
    invoicesQuotesOffersContractsEachFile: 'Invoices, quotes, offers, contracts… Each file is classified automatically.',
    noDocumentMatchesTheseFilters: 'No document matches these filters.',
    noDocumentYet: 'No document yet.',
    adjustOrClearTheFiltersTo: 'Adjust or clear the filters to see more results.',
    uploadYourInvoicesAndBusinessDocuments: 'Upload your invoices and business documents to classify them and find them in one click.',
    uploadADocument: 'Upload a document',
    shown: 'shown',
    ofTotal: 'of {{total}}',
    deselectAllSize: 'Deselect all ({{size}})',
    loading: 'Loading…',
    loadMore: 'Load more',
    deleteTheDocument: 'Delete the document?',
    vWillBePermanentlyDeleted: '“{{v}}” will be permanently deleted.',
    sendAllTheDisplayedDocuments: 'Send all the displayed documents?',
    theLengthDocumentSCurrentlyDisplayed: 'The {{length}} document(s) currently displayed will be attached to the email. Select documents beforehand to send only some of them.',
    continue: 'Continue',
    emailSentFromFromEmail: 'Email sent from {{from_email}}.',
    emailSent: 'Email sent.',
    selectAll: 'Select all',
    document: 'Document',
    amount: 'Amount',
    actions: 'Actions',
    selectV: 'Select {{v}}',
    download: 'Download',
    edit: 'Edit',
    delete: 'Delete',
  },
  sendByEmailModal: {
    baseUnverified: '{{base}} — unverified',
    invalidAddressBad: 'Invalid address: {{bad}}',
    removeAddr: 'Remove {{addr}}',
    noDocumentToSend: 'No document to send.',
    addAtLeastOneRecipient: 'Add at least one recipient.',
    chooseASendingMailboxSender: 'Choose a sending mailbox (sender).',
    enterASubject: 'Enter a subject.',
    theReplyToAddressIsInvalid: 'The “Reply-to” address is invalid.',
    sendingTheEmailFailedTryAgain: 'Sending the email failed. Try again.',
    sendByEmail: 'Send by email',
    cancel: 'Cancel',
    sending: 'Sending…',
    send: 'Send',
    asAn: 'as an',
    attachment: 'attachment',
    separateMultipleAddressesWithAComma: 'Separate multiple addresses with a comma or Enter.',
    cc: 'Cc',
    sender: 'Sender',
    noConnectedMailboxConnectAMailbox: 'No connected mailbox — connect a mailbox in Email › Mailboxes.',
    theSelectedMailboxIsTheReal: 'The selected mailbox is the real sender.',
    chooseAMailbox: '— Choose a mailbox —',
    loading: 'Loading…',
    displayName: 'Display name',
    optionalSenderName: 'Optional — sender name.',
    yourNameCompany: 'Your name / company',
    replyTo: 'Reply to',
    optionalReplyAddress: 'Optional — reply address.',
    topic: 'Topic',
    yourDocuments: 'Your documents',
    message: 'Message',
    helloPleaseFindAttachedTheRequested: 'Hello,\n\nPlease find attached the requested documents.',
    bundleIntoASingleZipFile: 'Bundle into a single ZIP file',
    nameExampleCom: 'name@example.com',
  },
};

export default en;
